import React, { useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { applyAmplifyAuthSurfaceVars } from './amplifyAuthSurface';
import { createAppTheme } from './createAppTheme';
import {
  readStoredThemeMode,
  subscribeThemeModeChange,
  type ThemeMode,
} from './themeStorage';

interface AppThemeProviderProps {
  children: React.ReactNode;
}

/**
 * MUI theme surface. Reads mode from localStorage and stays in sync with
 * AppContext via themeStorage events (avoids useApp here — prevents provider
 * ordering / HMR circular-import issues).
 */
export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => readStoredThemeMode());

  useEffect(() => subscribeThemeModeChange(setMode), []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    document.body.setAttribute('data-theme', mode);
    applyAmplifyAuthSurfaceVars(mode);
  }, [mode]);

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline enableColorScheme />
        {children}
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default AppThemeProvider;
